import { useEffect } from 'react'
import { LaunchQueueHandler } from '@/app/LaunchQueueHandler'
import { BrowserRouter, HashRouter } from 'react-router-dom'
import { platform } from '@/shared/lib/platform/platform'
import { Sentry } from '@/shared/lib/sentry'
import { PosthogIdentitySync } from '@/shared/lib/posthog/PosthogIdentitySync'
import { isPosthogConfigured } from '@/shared/lib/posthog/posthogEnv'
import { PosthogPageView } from '@/shared/lib/posthog/PosthogPageView'
import { ThemeLoader } from '@/shared/lib/ThemeLoader'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { usePatientStore } from '@/features/patients/model/patientStore'
import { useExamStore } from '@/features/exams/model/examStore'
import { useCalendarStore } from '@/features/calendar/model/calendarStore'
import { useClinicalDocumentStore } from '@/features/clinicalDocuments/model/clinicalDocumentStore'
import { useOutboxStore } from '@/features/offline/model/outboxStore'
import {
  installMemorySyncLifecycle,
  uninstallMemorySyncLifecycle,
} from '@/features/local-ai/memorySyncService'
import { ConfirmDialogProvider } from '@/shared/ui/ConfirmDialogProvider'
import { SwRefreshPrompt } from '@/features/sw/SwRefreshPrompt'
import { AppRoutes } from '@/app/routes'
import { checkForPwaUpdateAndToast } from '@/shared/pwa/checkForPwaUpdateAndToast'
import { SYNAP_PWA_VERSION_CHECK_INTERVAL_MS } from '@/shared/pwa/pwaUpdateConstants'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { E2EPlaywrightBridge } from '@/features/e2e/E2EPlaywrightBridge'
import { ScreenshotPlaywrightBridge } from '@/features/screenshot/ScreenshotPlaywrightBridge'
import { Toaster } from '@/shared/ui/toast'
import { PdfShareSheet } from '@/shared/ui/PdfShareSheet'
import { DocumentShareSheet } from '@/features/clinicalDocuments/ui/DocumentShareSheet'
import { OverlayHistoryBridge } from '@/shared/lib/overlay/OverlayHistoryBridge'
import { SplashScreen } from '@/shared/ui/SplashScreen'
import { hideSplashScreen } from '@/shared/lib/platform/splashScreen'
import { isChunkLoadError, recoverFromStaleDeployment } from '@/shared/pwa/staleDeploymentRecovery'

/** Fallback do ErrorBoundary: esconde a splash nativa para a tela de erro ficar visível. */
const SentryFallback = () => {
  useEffect(() => {
    void hideSplashScreen().catch(() => {})
  }, [])

  return (
    <div
      className="flex min-h-dvh items-center justify-center p-4"
      style={{ background: 'var(--color-page)' }}
    >
      <div className="max-w-sm text-center">
        <p className="mb-2 font-semibold text-[var(--color-text-primary)]">Ocorreu um erro inesperado.</p>
        <p className="mb-4 text-sm text-[var(--color-text-muted)]">
          O erro foi reportado automaticamente. Tente recarregar a página.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-2 text-sm font-semibold text-[var(--color-text-secondary)]"
        >
          Recarregar
        </button>
      </div>
    </div>
  )
}

/**
 * No app Capacitor nativo, o servidor local do WebView (androidScheme:'https', sem fallback SPA)
 * devolve 404 real ao recarregar numa rota profunda — o que acontece quando o Android mata o
 * processo em background (ex.: minimizar pra autorizar no app VIDaaS) e o WebView é restaurado.
 * HashRouter evita esse recarregamento de rota via servidor (tudo depois de `#` é client-side).
 * Na PWA/web mantemos BrowserRouter (URLs limpas, SEO, deep links reais).
 */
const Router = platform.isNative() ? HashRouter : BrowserRouter

/**
 * O retorno OAuth (VIDaaS / ITI CAS) abre a app numa aba/custom-tab só para capturar o `code` e
 * devolvê-lo ao fluxo de assinatura. Mostrar o splash completo aí faz o usuário perceber que ficou
 * "preso no splash". Detetamos a rota de callback (pathname na PWA/web, hash no app nativo) e pulamos
 * o splash para a página de callback aparecer de imediato. O balcão `/rx/:token` também
 * pula o splash: a farmácia cai direto no PIN, sem esperar a sessão.
 */
const isOAuthCallbackLocation = (): boolean => {
  try {
    const { pathname, hash } = window.location
    const target = `${pathname} ${hash}`
    return (
      /\/auth\/(vidaas|iti)\/callback/.test(target) ||
      /\/rx\/[^/?#\s]+/.test(target) ||
      /\/r\/[^/?#\s]+/.test(target)
    )
  } catch {
    return false
  }
}

const App = () => {
  const restoreSession = useSessionStore((s) => s.restoreSession)
  const user = useSessionStore((s) => s.user)
  const sessionRestored = useSessionStore((s) => s.sessionRestored)
  const fetchPatients = usePatientStore((s) => s.fetchPatients)
  const fetchExams = useExamStore((s) => s.fetchExams)
  const fetchEvents = useCalendarStore((s) => s.fetchEvents)
  const fetchDocs = useClinicalDocumentStore((s) => s.fetchData)
  const setupOutboxAutoRetry = useOutboxStore((s) => s.setupAutoRetry)
  const teardownOutboxAutoRetry = useOutboxStore((s) => s.teardownAutoRetry)
  const retryOutbox = useOutboxStore((s) => s.retryAll)
  const outboxLastSuccessAt = useOutboxStore((s) => s.lastSuccessAt)

  useEffect(() => {
    void restoreSession()
  }, [restoreSession])

  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        event.preventDefault()
        recoverFromStaleDeployment()
      }
    }
    window.addEventListener('unhandledrejection', onRejection)
    return () => window.removeEventListener('unhandledrejection', onRejection)
  }, [])

  useEffect(() => {
    if (!user) return
    setupOutboxAutoRetry()
    void retryOutbox()
    installMemorySyncLifecycle()
    return () => {
      teardownOutboxAutoRetry()
      uninstallMemorySyncLifecycle()
    }
  }, [user, setupOutboxAutoRetry, teardownOutboxAutoRetry, retryOutbox])

  useEffect(() => {
    if (!user) return
    void Promise.all([fetchPatients(), fetchExams(), fetchEvents(), fetchDocs()])
  }, [user, sessionRestored, outboxLastSuccessAt, fetchPatients, fetchExams, fetchEvents, fetchDocs])

  useEffect(() => {
    if (!user || !sessionRestored || isScreenshotFixtureMode()) return
    const runCheck = () => {
      void checkForPwaUpdateAndToast()
    }
    const initial = window.setTimeout(runCheck, 1500)
    const periodic = window.setInterval(runCheck, SYNAP_PWA_VERSION_CHECK_INTERVAL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') runCheck()
    }
    const onFocus = () => {
      runCheck()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearTimeout(initial)
      window.clearInterval(periodic)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
    }
  }, [user, sessionRestored])

  return (
    <Sentry.ErrorBoundary
      fallback={<SentryFallback />}
      beforeCapture={(scope) => {
        scope.setTag('error_boundary', 'root')
      }}
    >
      <SplashScreen ready={sessionRestored} skip={isOAuthCallbackLocation()} />
      <ConfirmDialogProvider>
        <Router>
          <OverlayHistoryBridge />
          <LaunchQueueHandler />
          <Toaster />
          <PdfShareSheet />
          <DocumentShareSheet />
          <ThemeLoader />
          {isPosthogConfigured() ? (
            <>
              <PosthogIdentitySync />
              <PosthogPageView />
            </>
          ) : null}
          <SwRefreshPrompt />
          <E2EPlaywrightBridge />
          {isScreenshotFixtureMode() ? <ScreenshotPlaywrightBridge /> : null}
          <AppRoutes />
        </Router>
      </ConfirmDialogProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
